import { Paper } from "@mui/material";
import { GridApi, RowClassParams, RowStyle } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useState } from "react";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { useSupplierHeatsTableData } from "../hooks/useSupplierHeatsTableData";
import { SupplierHeatRow } from "../models/ChemistryDraft";
import { getRevisionReason } from "./AcknowledgeButton";

type Props = {
	rows: SupplierHeatRow[];
	isLoading: boolean;
	quickFilter: string;
	selectedReportHeatId: number | null;
	canAcknowledge: boolean;
	isAcknowledging: boolean;
	onSelectHeat: (heat: SupplierHeatRow) => void;
	onToggleAcknowledge: (heat: SupplierHeatRow) => void;
};

export const SupplierHeatsTable = ({
	rows,
	isLoading,
	quickFilter,
	selectedReportHeatId,
	canAcknowledge,
	isAcknowledging,
	onSelectHeat,
	onToggleAcknowledge,
}: Props) => {
	const [gridApi, setGridApi] = useState<GridApi | null>(null);

	const { columnDefs } = useSupplierHeatsTableData({
		canAcknowledge,
		isAcknowledging,
		onToggleAcknowledge,
	});
	const { defaultColDef } = useGridDefinitions({});

	const getRowStyle = (
		params: RowClassParams<SupplierHeatRow>,
	): RowStyle | undefined => {
		const status =
			params.data?.AcknowledgedAt != null
				? "ACKNOWLEDGED"
				: getRevisionReason(params.data!)
					? "REVISION_REQUIRED"
					: "PENDING";

		return {
			...(params.data?.ReportHeatId === selectedReportHeatId && {
				backgroundColor: "#e3f2fd",
			}),
			...(params.data?.AcknowledgedAt == null && { fontWeight: 700 }),
			...(params.data &&
				status === "REVISION_REQUIRED" && {
					boxShadow: `inset 3px 0 0 #B45309`,
				}),
		};
	};

	useEffect(() => {
		gridApi?.redrawRows();
	}, [gridApi, selectedReportHeatId]);

	return (
		<Paper
			elevation={2}
			sx={{
				height: "100%",
				width: "100%",
				"& .ag-cell": { fontSize: 11 },
			}}
		>
			<AgGridReact<SupplierHeatRow>
				loading={isLoading}
				rowData={rows}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				quickFilterText={quickFilter}
				rowHeight={32}
				headerHeight={34}
				animateRows
				tooltipShowDelay={0}
				loadingOverlayComponent={CenteredLazyLoading}
				getRowId={(params) => String(params.data.ReportHeatId)}
				getRowStyle={getRowStyle}
				onGridReady={(params) => setGridApi(params.api)}
				onCellClicked={(event) => event.data && onSelectHeat(event.data)}
			/>
		</Paper>
	);
};
